import React, { useState, useEffect } from "react";
import axios from "axios";
import "./PerformanceReport.css";
import ClassSectionDetails from "./ClassSectionDetails"; // import details component
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";

function PerformanceReport() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [classSectionData, setClassSectionData] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 👇 show details inline instead of navigation
  const [viewDetails, setViewDetails] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const classRes = await axios.get(
          "https://aadyaacademy.org/api/classes-sections"
        );
        if (Array.isArray(classRes.data.data)) {
          const flattened = classRes.data.data.flatMap((item) =>
            item.sections.map((sec) => ({
              class: item.class,
              section: sec,
            }))
          );
          setClassSectionData(flattened);
        } else {
          throw new Error("Invalid classes-sections data format");
        }

        const testRes = await axios.get("https://aadyaacademy.org/api/tests");
        if (Array.isArray(testRes.data.data)) {
          setTests(testRes.data.data);
        } else {
          throw new Error("Invalid tests data format");
        }
      } catch (err) {
        setError("Failed to fetch data. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getAllClasses = () => [
    ...new Set(classSectionData.map((data) => data.class)),
  ];
  const getAllSections = () => [
    ...new Set(classSectionData.map((data) => data.section)),
  ];

  const handleViewClick = (classValue, sectionValue) => {
    setViewDetails({ classValue, sectionValue }); // store details to render
  };

  const filteredData = classSectionData.filter((data) => {
    const matchesSearch =
      data.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
      data.section.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = selectedClass ? data.class === selectedClass : true;
    const matchesSection = selectedSection
      ? data.section === selectedSection
      : true;
    return matchesSearch && matchesClass && matchesSection;
  });

  return (
    <div
      className="performance-report"
      style={{
        fontSize: "14px",
        height: "calc(100vh - 60px)",
        overflowY: "auto",
        padding: "12px",
      }}
    >
      {!viewDetails ? (
        <>
          <div className="table-header" style={{ marginBottom: "12px" }}>
            <h3 style={{ fontSize: "18px" }}>Performance Report</h3>
            <div
              className="table-header-btm"
              style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}
            >
              <input
                type="text"
                placeholder="Search by class or section"
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  fontSize: "14px",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              />
              <select
                className="class-filter"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                style={{
                  fontSize: "14px",
                  padding: "8px",
                  borderRadius: "6px",
                }}
              >
                <option value="">All Classes</option>
                {getAllClasses().map((cls, i) => (
                  <option key={i} value={cls}>
                    {cls}
                  </option>
                ))}
              </select>
              <select
                className="section-filter"
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                style={{
                  fontSize: "14px",
                  padding: "8px",
                  borderRadius: "6px",
                }}
              >
                <option value="">All Sections</option>
                {getAllSections().map((sec, i) => (
                  <option key={i} value={sec}>
                    {sec}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && <p style={{ fontSize: "14px", color: "red" }}>{error}</p>}
          {loading ? (
            <p style={{ fontSize: "14px" }}>Loading...</p>
          ) : (
            <table
              style={{
                fontSize: "14px",
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                    }}
                  >
                    Class
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                    }}
                  >
                    Section
                  </th>
                  <th
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #ccc",
                      textAlign: "left",
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((data, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      {data.class}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      {data.section}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <span
                        style={{
                          color: "#007bff",
                          cursor: "pointer",
                          fontWeight: "500",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                        onClick={() =>
                          handleViewClick(data.class, data.section)
                        }
                      >
                        <FontAwesomeIcon icon={faEye} /> View
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : (
        <ClassSectionDetails
          classValue={viewDetails.classValue}
          sectionValue={viewDetails.sectionValue}
          onBack={() => setViewDetails(null)} // back to table
        />
      )}
    </div>
  );
}

export default PerformanceReport;
